/**
 * Settings Route Guard
 *
 * Route-level wrapper that prevents users from reaching individual settings
 * pages they don't have permission to act on. Looks up the current pathname
 * in `settingsConfig` and, if the matched item/group is inaccessible to the
 * current user, renders a friendly redirect to the Settings overview instead
 * of the actual page (where the user would otherwise just hit an error when
 * they tried to do something).
 *
 * Applied as a wrapper around the `/prioritization/settings/*` subtree in
 * PrioritizationApp.
 */

import { Link, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  isSettingsItemVisible,
  matchSettingsPath,
  type SettingsAccessContext,
} from './settingsConfig';
import { settingsPanel } from './settingsUi';

function NoAccessPanel({
  groupTitle,
  itemName,
}: {
  groupTitle?: string;
  itemName?: string;
}) {
  const label = itemName
    ? `"${itemName}"`
    : groupTitle
    ? `the ${groupTitle} settings`
    : 'this settings page';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldAlert className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Not available"
        subtitle="You don't have permission to view this settings page."
      />
      <div className={settingsPanel}>
        <p>
          You don't have permission to access {label}. If you believe you should
          have access, contact a workspace administrator.
        </p>
        <div className="mt-4">
          <Link
            to="/prioritization/settings"
            className="text-sm font-medium text-primary-600 hover:underline"
          >
            Back to Settings overview →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function SettingsRouteGuard() {
  const location = useLocation();
  const currentRole = useUserStore((s) => s.currentRole);
  const canAny = useUserStore((s) => s.canAny);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);

  // While permissions are still hydrating, show a neutral loading state.
  // We intentionally do NOT pass through to the child route here — doing so
  // would allow any user to reach admin pages during the brief hydration window
  // before role assignments are resolved from the DB.
  const authStillHydrating =
    !permissionsLoaded && !currentRole && (assignedRoleNames?.length ?? 0) === 0;
  if (authStillHydrating) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Loading…
      </div>
    );
  }

  const isPlatformAdmin =
    currentRole === 'platform-admin' || currentRole === 'system-admin';
  const ctx: SettingsAccessContext = { canAny, isPlatformAdmin };

  // `/prioritization/settings` (index) is always allowed — the SettingsIndex
  // page itself handles filtering and empty-state.
  if (location.pathname === '/prioritization/settings' || location.pathname === '/prioritization/settings/') {
    return (
      <div className="pp-settings">
        <Outlet />
      </div>
    );
  }

  const match = matchSettingsPath(location.pathname);

  // Unknown / unmanaged path — let it render (will 404 or be handled elsewhere).
  if (!match) {
    return (
      <div className="pp-settings">
        <Outlet />
      </div>
    );
  }

  // Group-only path (index of a group). Let the group page component handle
  // empty-state itself (SettingsGroupPage already filters + shows a message).
  if (!match.item) {
    if (match.group.adminOnly && !isPlatformAdmin) {
      // Allow through if the user has access to at least one item in the group
      // (e.g. workspace admins with admin.settings can reach Developer & Admin
      // even though the group is marked adminOnly for sidebar-visibility purposes).
      const hasAnyItemAccess = match.group.items.some((item) =>
        isSettingsItemVisible(item, ctx),
      );
      if (!hasAnyItemAccess) {
        return (
          <div className="pp-settings">
            <NoAccessPanel groupTitle={match.group.title} />
          </div>
        );
      }
    }
    return (
      <div className="pp-settings">
        <Outlet />
      </div>
    );
  }

  // Item-level access check.
  if (!isSettingsItemVisible(match.item, ctx)) {
    return (
      <div className="pp-settings">
        <NoAccessPanel groupTitle={match.group.title} itemName={match.item.name} />
      </div>
    );
  }

  return (
    <div className="pp-settings">
      <Outlet />
    </div>
  );
}
